// Personal API keys — long-lived bearer credentials that authenticate
// as the issuing user ("call Reflex as me"). The plaintext token
// (`rfx_<hex>`) is returned exactly once at creation; only a SHA-256
// hash is stored server-side. A key grants the same access as the
// user's session: org scoping still comes from the request's
// `x-organization-id` header and is validated against the user's
// memberships.

import { z } from 'zod';
import { coreIds } from './ids.js';

/** Prefix carried by every personal API key token (not the row id). */
export const PERSONAL_API_KEY_TOKEN_PREFIX = 'rfx_';

/**
 * Whether a bearer token is a personal API key rather than a session token.
 * Lives beside the prefix so the one rule has one home: the auth middleware
 * routes on it to pick a resolver, and surfaces that report how a request
 * arrived read the same answer instead of re-deriving it.
 */
export function isPersonalApiKeyToken(token: string): boolean {
  return token.startsWith(PERSONAL_API_KEY_TOKEN_PREFIX);
}

export const PersonalApiKeySchema = z.object({
  id: coreIds.schema('personalApiKey'),
  name: z.string(),
  /**
   * Non-secret display fragment of the token (`rfx_` + first 6 hex
   * chars) so users can match a key in their env to a row in the list.
   */
  tokenPrefix: z.string(),
  createdAt: z.number(),
  /** Epoch-ms of the most recent authenticated use, null if never used. */
  lastUsedAt: z.number().nullable(),
});
export type PersonalApiKey = z.infer<typeof PersonalApiKeySchema>;

export const CreatePersonalApiKeyRequestSchema = z.object({
  name: z.string().trim().min(1).max(100),
});
export type CreatePersonalApiKeyRequest = z.infer<typeof CreatePersonalApiKeyRequestSchema>;

export const CreatePersonalApiKeyResponseSchema = z.object({
  key: PersonalApiKeySchema,
  /** Plaintext token. Shown once; never retrievable again. */
  token: z.string(),
});
export type CreatePersonalApiKeyResponse = z.infer<typeof CreatePersonalApiKeyResponseSchema>;

export const PersonalApiKeysListResponseSchema = z.object({
  keys: z.array(PersonalApiKeySchema),
});
export type PersonalApiKeysListResponse = z.infer<typeof PersonalApiKeysListResponseSchema>;
